/**
 * ISA-95 sidebar group inference.
 *
 * Every module manifest already carries `isa95Tier` and `productFamily` (see
 * docs/module-categorization.md — the authoritative taxonomy). The sidebar
 * buckets modules into five ordered ISA-95 groups; historically it inferred the
 * group from a substring match on `moduleId` alone, which dumped ~26 plant-floor
 * / plan / platform modules (historian, uns, lims, plm, controls, spatial, …)
 * into a giant Admin catch-all even though their manifest metadata says exactly
 * where they belong.
 *
 * Resolution order (first match wins):
 *   1. An explicit `UiMenuItem.group` on the menu entry (honored by the caller).
 *   2. A gateway-resolved `group` on the module metadata (`isaGroup` in the UI
 *      manifest) — the authoritative sidebar bucket the backend computes from
 *      the canonical taxonomy (docs/module-categorization.md). Preferred over
 *      the raw family/tier because the doc taxonomy, not the noisy family
 *      field, is the design authority (e.g. Materials is `mes-mom` but belongs
 *      in `plan`; Compliance is `mes-mom` but belongs in `people`).
 *   3. `productFamily` → group, for the families that map unambiguously.
 *   4. `isa95Tier` → group, for the plant-floor tiers (level-0/1/2).
 *   5. A substring match on `moduleId` (covers modules whose manifest omits the
 *      metadata — e.g. the dev-fallback stubs and the ambiguous erp-business
 *      family that splits across plan/commerce/people).
 *   6. `admin` — the genuine platform / admin-console catch-all.
 *
 * Groups (in display order):
 *   operate — runs the line: Level 0–2 plant floor (SCADA, Historian, Alarms,
 *     Vision, Controls, UNS, Edge/EdgeFleet, Designer HMI) + Level-3 MES
 *     execution (Production, Batch, EBR, Quality, LIMS, Maintenance).
 *   plan — feeds the line: Planning, Materials, Inventory, Procurement, PLM.
 *   commerce — sells the goods: Sales, CRM, Costing, Finance, Sustainability,
 *     Marketplace.
 *   people — runs the org: HR, Workflow, Compliance.
 *   admin — runs the system: Admin, Multisite, Integration, Analytics, Copilot,
 *     Predictive, Digital Twin, Collaboration, and the platform-service
 *     consoles.
 */

import type { Isa95Tier, ProductFamily } from '@tyndo/types';

export type IsaGroup = 'operate' | 'plan' | 'commerce' | 'people' | 'admin';

/**
 * The subset of a module manifest the grouping consults. Kept minimal (a
 * structural shape, not the full registry entry) so this stays a pure,
 * unit-testable function independent of the registry.
 */
export interface IsaGroupModuleMeta {
 /**
  * Sidebar group the gateway already resolved from the canonical taxonomy
  * (the `isaGroup` field of the UI manifest). When present it is
  * authoritative — the backend computes it per docs/module-categorization.md,
  * which is the design authority for the bucket even where the raw family
  * field is ambiguous.
  */
 isaGroup?: IsaGroup;
 isa95Tier?: Isa95Tier;
 productFamily?: ProductFamily;
}

/**
 * Product-family → group, for the families that map to a single ISA group.
 *
 * Two families are deliberately absent because they split across groups, so a
 * single mapping would be wrong for part of each — they fall through to the
 * substring rules below, which already split them correctly:
 *   • `erp-business` spans plan (Materials, Inventory, Planning), commerce
 *     (Finance, Sales, CRM, Costing) and people (HR).
 *   • `intelligence-sustainability` spans admin (Predictive, DigitalTwin,
 *     Analytics, Copilot) and commerce/ESG (Sustainability, PcfWire).
 */
const GROUP_BY_PRODUCT_FAMILY: Partial<Record<ProductFamily, IsaGroup>> = {
 'plant-floor-edge': 'operate',
 'mes-mom': 'operate',
 plm: 'plan',
 'platform-distribution': 'admin',
};

/**
 * ISA-95 tier → group, used only when the product family didn't resolve. Tier
 * cleanly places the plant-floor levels; level-3/4 are ambiguous (MES execution
 * vs. ERP business vs. PLM) so they are intentionally left to the family map and
 * the substring fallback.
 */
const GROUP_BY_ISA_TIER: Partial<Record<Isa95Tier, IsaGroup>> = {
 'level-0': 'operate',
 'level-1': 'operate',
 'level-2': 'operate',
};

const FALLBACK_GROUP_BY_MODULE_SUBSTR: Array<{ match: string; group: IsaGroup }> = [
 // Operate — plant floor + MES execution.
 { match: 'production', group: 'operate' },
 { match: 'equipment', group: 'operate' },
 { match: 'alarms', group: 'operate' },
 { match: 'batch', group: 'operate' },
 { match: 'ebr', group: 'operate' },
 { match: 'quality', group: 'operate' },
 { match: 'lims', group: 'operate' },
 { match: 'maintenance', group: 'operate' },
 { match: 'scada', group: 'operate' },
 { match: 'vision', group: 'operate' },
 { match: 'historian', group: 'operate' },
 { match: 'controls', group: 'operate' },
 { match: 'opcua', group: 'operate' },
 { match: 'uns', group: 'operate' },
 { match: 'unifiednamespace', group: 'operate' },
 { match: 'edgefleet', group: 'operate' },
 { match: 'edge', group: 'operate' },
 { match: 'writefirewall', group: 'operate' },
 { match: 'blackout', group: 'operate' },
 { match: 'motiongenome', group: 'operate' },
 { match: 'responseatlas', group: 'operate' },
 { match: 'flightsimulator', group: 'operate' },

 // Plan — feeds the line.
 { match: 'planning', group: 'plan' },
 { match: 'inventory', group: 'plan' },
 { match: 'procurement', group: 'plan' },
 { match: 'materials', group: 'plan' },
 { match: 'plm', group: 'plan' },

 // Commerce — sells the goods.
 { match: 'finance', group: 'commerce' },
 { match: 'sales', group: 'commerce' },
 { match: 'crm', group: 'commerce' },
 { match: 'costing', group: 'commerce' },
 { match: 'sustainability', group: 'commerce' },
 { match: 'pcfwire', group: 'commerce' },
 { match: 'productpassport', group: 'commerce' },
 { match: 'marketplace', group: 'commerce' },

 // People — runs the org.
 { match: 'hr', group: 'people' },
 { match: 'workflow', group: 'people' },
 { match: 'compliance', group: 'people' },
 { match: 'competencepassport', group: 'people' },

 // Admin — runs the system / platform consoles.
 { match: 'analytics', group: 'admin' },
 { match: 'admin', group: 'admin' },
 { match: 'designer', group: 'admin' },
 { match: 'multisite', group: 'admin' },
 { match: 'integration', group: 'admin' },
 { match: 'copilot', group: 'admin' },
 { match: 'predictive', group: 'admin' },
 { match: 'digital-twin', group: 'admin' },
 { match: 'digitaltwin', group: 'admin' },
 { match: 'plantsandbox', group: 'admin' },
 { match: 'causalattribution', group: 'admin' },
 { match: 'handshakeledger', group: 'admin' },
 { match: 'digesttreaty', group: 'admin' },
 { match: 'federatedworkorder', group: 'admin' },
 { match: 'collaboration', group: 'admin' },
];

/**
 * Infer a module's ISA-95 sidebar group. Prefers the manifest's own
 * `productFamily` / `isa95Tier` metadata (authoritative), then falls back to a
 * substring match on `moduleId` for manifests that omit the metadata (dev
 * fallbacks, ambiguous erp-business modules), and finally to `admin`.
 */
export function inferIsaGroup(
 moduleId: string | undefined,
 meta?: IsaGroupModuleMeta,
): IsaGroup {
 // 2. Gateway-resolved group — authoritative when the manifest carries it.
 if (meta?.isaGroup && ISA_GROUP_ORDER.includes(meta.isaGroup)) {
 return meta.isaGroup;
 }

 // 3. Product family — the coarse family map, most reliable when present.
 if (meta?.productFamily) {
 const byFamily = GROUP_BY_PRODUCT_FAMILY[meta.productFamily];
 if (byFamily) return byFamily;
 }

 // 4. ISA-95 tier — places the plant-floor levels when the family didn't.
 if (meta?.isa95Tier) {
 const byTier = GROUP_BY_ISA_TIER[meta.isa95Tier];
 if (byTier) return byTier;
 }

 // 5. Substring match on moduleId — covers metadata-less manifests.
 if (moduleId) {
 const lower = moduleId.toLowerCase();
 for (const { match, group } of FALLBACK_GROUP_BY_MODULE_SUBSTR) {
 if (lower.includes(match)) return group;
 }
 }

 // 6. Genuine admin / platform catch-all.
 return 'admin';
}

export const ISA_GROUP_ORDER: IsaGroup[] = [
 'operate',
 'plan',
 'commerce',
 'people',
 'admin',
];

export const ISA_GROUP_LABEL: Record<IsaGroup, string> = {
 operate: 'Operate',
 plan: 'Plan',
 commerce: 'Commerce',
 people: 'People',
 admin: 'Admin',
};
